
import Vue from 'vue';
import { message } from 'ant-design-vue';
import { getWorkList } from '../api';
import HomeFooter from '../components-pc/HomeFooter.vue';
import PcPageLoading from '../components-pc/PcPageLoading.vue';

interface CategoryItem {
  workType: number;
  name: string;
  count: number;
}

interface WorkItem {
  audio_type: string | null;
  class: string;
  createtime: string;
  education: string;
  enable: string;
  exp_url: string | null;
  explanation: string | null;
  files: string;
  id: string;
  introduction: string;
  name: string;
  school: string;
  teacher: string;
  title: string;
  vote_num: string;
  votes: number;
  work_type: string;
}

interface WorkTypeInfo {
  workType: number;
  name: string;
  tagClass: string;
  coverClass: string;
  isVideo: boolean;
}

const workTypeList: WorkTypeInfo[] = [
  {
    workType: 1,
    name: '摄影',
    tagClass: 'tag-photo',
    coverClass: 'cover-street',
    isVideo: false,
  },
  {
    workType: 3,
    name: '短视频',
    tagClass: 'tag-video',
    coverClass: 'cover-flight',
    isVideo: true,
  },
  {
    workType: 2,
    name: '视听',
    tagClass: 'tag-audio',
    coverClass: 'cover-flower',
    isVideo: false,
  },
  {
    workType: 4,
    name: '图文报道',
    tagClass: 'tag-report',
    coverClass: 'cover-lake',
    isVideo: false,
  },
  {
    workType: 5,
    name: '人工智能',
    tagClass: 'tag-ai',
    coverClass: 'cover-field',
    isVideo: true,
  },
];

const defaultWorkTypeInfo = workTypeList[0];

export default Vue.extend({
  name: 'WorksPc',
  components: {
    HomeFooter,
    PcPageLoading,
  },
  data() {
    return {
      activeWorkType: 0,
      loading: false,
      categoryList: [
        { workType: 0, name: '全部', count: 0 },
        ...workTypeList.map((item) => ({
          workType: item.workType,
          name: item.name,
          count: 0,
        })),
      ] as CategoryItem[],
      workList: [] as WorkItem[],
    };
  },
  computed: {
    displayWorks(): WorkItem[] {
      return this.workList;
    },
  },
  mounted() {
    this.fetchWorkList();
  },
  methods: {
    handleCategoryChange(workType: number) {
      if (this.activeWorkType === workType || this.loading) {
        return;
      }

      this.activeWorkType = workType;
      this.fetchWorkList();
    },
    async fetchWorkList() {
      this.loading = true;

      try {
        const res: any = await getWorkList({
          workType: this.activeWorkType,
        });
        const list = Array.isArray(res.data) ? res.data : [];
        this.workList = list;
        this.updateCategoryCounts(list);
      } catch (err) {
        console.error('作品列表获取失败：', err);
        this.workList = [];
        this.updateCategoryCounts([]);
        message.error('作品列表获取失败，请稍后重试');
      } finally {
        this.loading = false;
      }
    },
    getWorkTypeInfo(workType: string | number): WorkTypeInfo {
      const normalizedWorkType = Number(workType);

      return (
        workTypeList.find((item) => item.workType === normalizedWorkType) ||
        defaultWorkTypeInfo
      );
    },
    getWorkTypeName(workType: string | number): string {
      return this.getWorkTypeInfo(workType).name;
    },
    getWorkTypeClass(workType: string | number): string {
      return this.getWorkTypeInfo(workType).tagClass;
    },
    getWorkTypeCoverClass(workType: string | number): string {
      return this.getWorkTypeInfo(workType).coverClass;
    },
    getStatusText(enable: string) {
      const status = Number(enable);

      if (status === 1) {
        return '审核已通过';
      }

      if (status === -1) {
        return '审核未通过';
      }

      return '待审核';
    },
    getStatusClass(enable: string) {
      const status = Number(enable);

      if (status === -1) {
        return 'status-failed';
      }

      if (status === 1) {
        return 'status-passed';
      }

      return 'status-pending';
    },
    getIsVideo(workType: string | number): boolean {
      return this.getWorkTypeInfo(workType).isVideo;
    },
    getHasVideoCoverStyle(workType: string | number): boolean {
      return [3, 5].includes(Number(workType));
    },
    getShowVideoCoverIcon(item: WorkItem): boolean {
      return (
        this.getHasVideoCoverStyle(item.work_type) &&
        Boolean(this.getCoverUrl(item))
      );
    },

    getCoverStyle(item: WorkItem) {
      const coverUrl = this.getCoverUrl(item);

      if (!coverUrl) {
        return undefined;
      }

      return {
        backgroundImage: `url(${coverUrl})`,
      };
    },
    getCoverUrl(item: WorkItem): string {
      const workType = Number(item.work_type);

      if (workType === 3 || workType === 5) {
        return item.explanation || '';
      }

      const firstFile = this.getFirstFile(item.files);

      if (workType === 4) {
        if (firstFile && typeof firstFile === 'object') {
          const { imageUrl } = firstFile as { imageUrl?: unknown };
          return typeof imageUrl === 'string' ? imageUrl : '';
        }

        return '';
      }

      return typeof firstFile === 'string' ? firstFile : '';
    },
    getFirstFile(files: string): unknown {
      if (!files) {
        return '';
      }

      try {
        const fileList = JSON.parse(files);
        if (!Array.isArray(fileList) || !fileList.length) {
          return '';
        }

        return fileList[0];
      } catch (err) {
        console.error('作品文件解析失败：', err);
        return '';
      }
    },
    updateCategoryCounts(list: WorkItem[]) {
      const nextCategoryList = this.categoryList.map((item) => {
        if (item.workType === 0) {
          return {
            ...item,
            count: this.activeWorkType === 0 ? list.length : item.count,
          };
        }

        const count =
          this.activeWorkType === 0 || this.activeWorkType === item.workType
            ? list.filter((work) => Number(work.work_type) === item.workType)
                .length
            : item.count;

        return {
          ...item,
          count,
        };
      });

      this.categoryList = nextCategoryList;
    },
    goDetail(item: WorkItem) {
      this.$router
        .push({
          path: '/cqcc/detail',
          query: {
            id: String(item.id),
            type: this.getIsVideo(item.work_type) ? 'video' : 'image',
          },
        })
        .catch((err: Error) => err);
    },
    goEdit(item: WorkItem) {
      const editPathMap: { [key: number]: string } = {
        1: '/cqcc/signup/photo',
        2: '/cqcc/signup/audio',
        3: '/cqcc/signup/video',
        4: '/cqcc/signup/report',
        5: '/cqcc/signup/ai',
      };
      const path = editPathMap[Number(item.work_type)];
      if (!path) {
        message.error('暂不支持编辑该作品类型');
        return;
      }

      this.$router
        .push({
          path,
          query: {
            id: String(item.id),
          },
        })
        .catch((err: Error) => err);
    },
  },
});
